import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import { useFeatureAccess } from '../hooks/useFeatureAccess';

interface FeatureRouteProps {
  children: React.ReactNode;
  requiredFeature?: string;
  requiredModulo?: string;
  featureName?: string; // Nome amigável do módulo/feature para exibir na mensagem
}

export default function FeatureRoute({ 
  children, 
  requiredFeature,
  requiredModulo,
  featureName 
}: FeatureRouteProps) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isIntegracoes, userRole } = useEmpresa();
  const { hasFeature, hasModulo, isLoading: featuresLoading } = useFeatureAccess();
  const location = useLocation();

  // Se ainda está carregando autenticação, mostrar loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Se não está autenticado, redirecionar para login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Se ainda está carregando features/módulos, mostrar loading
  if (featuresLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // 0. Verificação Estrita de RBAC por Perfil
  if (isIntegracoes && requiredModulo !== 'integracoes' && location.pathname !== '/' && !location.pathname.startsWith('/integracoes') && !location.pathname.startsWith('/profile')) {
    console.warn(`⚠️ [FeatureRoute] Acesso negado: Perfil Integrações restrito a Dashboard e Integrações`);
    return <Navigate to="/" replace />;
  }

  if (requiredModulo === 'integracoes' && !isIntegracoes) {
    console.warn(`⚠️ [FeatureRoute] Acesso negado: Módulo de Integrações exclusivo para perfil Integrações`);
    return <Navigate to="/" replace />;
  }

  if (userRole === 'AGENDADOR') {
    const allowedModulosForAgendador = ['gestao_organizacional', 'gestao_exames'];
    if (requiredModulo && !allowedModulosForAgendador.includes(requiredModulo)) {
      console.warn(`⚠️ [FeatureRoute] Acesso negado: Perfil Agendador não possui acesso ao módulo '${requiredModulo}'`);
      return <Navigate to="/" replace />;
    }
  } else if (userRole === 'TECNICO') {
    const allowedModulosForTecnico = ['gestao_compliance', 'gestao_epis', 'gestao_cipa'];
    if (requiredModulo && !allowedModulosForTecnico.includes(requiredModulo)) {
      console.warn(`⚠️ [FeatureRoute] Acesso negado: Perfil Técnico não possui acesso ao módulo '${requiredModulo}'`);
      return <Navigate to="/" replace />;
    }
  } else if (userRole === 'FINANCEIRO') {
    const allowedModulosForFinanceiro = ['financeiro'];
    if (requiredModulo && !allowedModulosForFinanceiro.includes(requiredModulo)) {
      console.warn(`⚠️ [FeatureRoute] Acesso negado: Perfil Financeiro não possui acesso ao módulo '${requiredModulo}'`);
      return <Navigate to="/" replace />;
    }
  }

  // 1. Verificação de Módulo Oficial (com compatibilidade inteligente de módulos legados)
  if (requiredModulo) {
    let moduloAtivo = hasModulo(requiredModulo);
    if (!moduloAtivo && requiredModulo === 'gestao_absenteismo') {
      moduloAtivo =
        hasModulo('gestao_atestados') ||
        hasFeature('atestados') ||
        hasFeature('absenteeism');
    }
    if (!moduloAtivo && requiredModulo === 'saude_corporativa') {
      moduloAtivo = hasFeature('prontuario_clinico') || hasFeature('saude_corporativa');
    }

    if (!moduloAtivo) {
      console.warn(`⚠️ [FeatureRoute] Acesso negado: Módulo '${featureName || requiredModulo}' não contratado pela empresa`);
      return <Navigate to="/" replace state={{ from: location.pathname }} />;
    }
  }

  // 2. Verificação de Feature Granular
  if (requiredFeature) {
    const hasRequiredFeature = requiredFeature === 'exames' 
      ? (hasFeature('exames') || hasFeature('clinical_exams') || hasFeature('online_management'))
      : hasFeature(requiredFeature);

    if (!hasRequiredFeature) {
      console.warn(`⚠️ [FeatureRoute] Acesso negado: Feature '${featureName || requiredFeature}' não disponível`);
      return <Navigate to="/" replace state={{ from: location.pathname }} />;
    }
  }
  
  return <>{children}</>;
}

